import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { Link } from "react-router-dom";
import { Footer, Navbar } from "../components";
import AdsBannerTop from "../components/AdsBannerTop";
import MovieCardSkeleton from "../components/MovieCardSkeleton";
import { TrendingMovies, nowPlayingMovies, nowPlayingTvShows, getTrendingAnime } from "../constants/api";
import { Play, Star, Film, Tv } from "lucide-react";

const HomePage = () => {
  const [trending, setTrending] = useState([]);
  const [movies, setMovies] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [anime, setAnime] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [trendingData, moviesData, tvData, animeData] = await Promise.all([
          TrendingMovies(),
          nowPlayingMovies(),
          nowPlayingTvShows(),
          getTrendingAnime(),
        ]);
        
        const trendingResults = trendingData?.results?.filter(item => item.backdrop_path) || [];
        setTrending(trendingResults);
        setMovies(moviesData?.results?.slice(0, 20) || []);
        setTvShows(tvData?.results?.slice(0, 20) || []);
        setAnime(animeData?.results?.slice(0, 20) || []);
      } catch (error) {
        console.error("Error fetching homepage data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Auto-rotate hero with smooth transition
  useEffect(() => {
    if (trending.length > 1) {
      const interval = setInterval(() => {
        setHeroIndex((prev) => {
          const next = (prev + 1) % Math.min(trending.length, 10);
          return next;
        });
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [trending.length]);

  const heroItem = trending[heroIndex];
  const mediaType = heroItem?.media_type === 'tv' ? 'TV Show' : heroItem?.media_type === 'movie' ? 'Movie' : '';
  const releaseDate = heroItem?.release_date || heroItem?.first_air_date;
  const year = releaseDate ? new Date(releaseDate).getFullYear() : '';
  const rating = heroItem?.vote_average?.toFixed(1);

  return (
    <>
      <Navbar />
      <AdsBannerTop />
      <div className="bg-black min-h-screen">
        {/* Hero Section */}
        {!loading && heroItem && (
          <section className="relative h-[80vh] min-h-[600px] max-h-[900px] overflow-hidden">
            <div
              key={heroIndex}
              className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
              style={{
                backgroundImage: `url(https://image.tmdb.org/t/p/original${heroItem.backdrop_path})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/40"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            </div>

            <div className="relative z-10 h-full flex items-center padding">
              <div className="max-w-3xl">
                <h1 className="text-white poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-4 leading-tight">
                  {heroItem.title || heroItem.name}
                </h1>
                <div className="flex items-center gap-3 mb-4 text-white/90 flex-wrap">
                  <span className="mont text-base">{mediaType}</span>
                  {year && (
                    <>
                      <span className="text-white/50">•</span>
                      <span className="mont text-base">{year}</span>
                    </>
                  )}
                  {rating && (
                    <>
                      <span className="text-white/50">•</span>
                      <div className="flex items-center gap-1.5">
                        <Star className="text-yellow-400" size={16} fill="#FBBF24" />
                        <span className="mont text-base font-semibold">{rating}</span>
                      </div>
                    </>
                  )}
                </div>
                <p className="text-white/80 mont text-base md:text-lg mb-8 line-clamp-3 max-w-2xl">
                  {heroItem.overview || 'Experience the best in entertainment.'}
                </p>
                <Link
                  to={heroItem.media_type === 'tv' ? `/tvshows/${heroItem.id}` : `/movies/${heroItem.id}`}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-red text-white mont font-semibold text-base rounded-lg hover:bg-red/90 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red/30"
                >
                  <Play size={20} fill="white" />
                  Watch Now
                </Link>
              </div>
            </div>

            {/* Hero Navigation Dots */}
            {trending.length > 1 && (
              <div className="absolute bottom-8 right-8 z-20 flex items-center gap-2">
                {Array.from({ length: Math.min(trending.length, 10) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setHeroIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === heroIndex ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/70'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Content Sections */}
        <div className="padding pt-12 pb-16">
          {/* Movies Section */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white mont font-bold text-2xl md:text-3xl">
                Trending Movies <span className="text-red">🔥</span>
              </h2>
              <Link
                to="/movies"
                className="text-white/60 hover:text-red mont font-medium text-sm transition-colors"
              >
                View All →
              </Link>
            </div>
            
            <div className="relative">
              {loading ? (
                <div className="grid lg:grid-cols-6 md:grid-cols-4 sm:grid-cols-3 grid-cols-2 gap-4 md:gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <MovieCardSkeleton key={index} />
                  ))}
                </div>
              ) : (
                <Swiper
                  spaceBetween={20}
                  slidesPerView={5}
                  navigation={true}
                  breakpoints={{
                    230: { slidesPerView: 2, spaceBetween: 8 },
                    480: { slidesPerView: 3, spaceBetween: 10 },
                    640: { slidesPerView: 3, spaceBetween: 12 },
                    768: { slidesPerView: 4, spaceBetween: 15 },
                    1000: { slidesPerView: 5, spaceBetween: 18 },
                    1280: { slidesPerView: 6, spaceBetween: 20 },
                  }}
                  modules={[Navigation]}
                  className="modern-swiper"
                >
                  {movies.filter(movie => movie.poster_path).map((movie) => (
                    <SwiperSlide key={movie.id} className="modern-movie-slide">
                      <Link to={`/movies/${movie.id}`} className="group">
                        <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                          <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                            <img
                              src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                              alt={movie.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                                <Play className="text-white" size={28} fill="white" />
                              </div>
                            </div>
                            <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                              <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                              <span className="text-white mont text-xs font-semibold">
                                {movie.vote_average?.toFixed(1)}
                              </span>
                            </div>
                          </div>
                          <div className="p-3">
                            <h3 className="text-white mont font-semibold text-[14px] leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                              {movie.title}
                            </h3>
                            {movie.release_date && (
                              <p className="text-white/50 mont text-xs mt-1">
                                {new Date(movie.release_date).getFullYear()}
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
          </div>

          {/* TV Shows Section */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white mont font-bold text-2xl md:text-3xl">
                Trending TV Shows <span className="text-red">🔥</span>
              </h2>
              <Link
                to="/tvshows"
                className="text-white/60 hover:text-red mont font-medium text-sm transition-colors"
              >
                View All →
              </Link>
            </div>
            
            <div className="relative">
              {loading ? (
                <div className="grid lg:grid-cols-6 md:grid-cols-4 sm:grid-cols-3 grid-cols-2 gap-4 md:gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <MovieCardSkeleton key={index} />
                  ))}
                </div>
              ) : (
                <Swiper
                  spaceBetween={20}
                  slidesPerView={5}
                  navigation={true}
                  breakpoints={{
                    230: { slidesPerView: 2, spaceBetween: 8 },
                    480: { slidesPerView: 3, spaceBetween: 10 },
                    640: { slidesPerView: 3, spaceBetween: 12 },
                    768: { slidesPerView: 4, spaceBetween: 15 },
                    1000: { slidesPerView: 5, spaceBetween: 18 },
                    1280: { slidesPerView: 6, spaceBetween: 20 },
                  }}
                  modules={[Navigation]}
                  className="modern-swiper"
                >
                  {tvShows.filter(show => show.poster_path).map((show) => (
                    <SwiperSlide key={show.id} className="modern-movie-slide">
                      <Link to={`/tvshows/${show.id}`} className="group">
                        <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                          <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                            <img
                              src={`https://image.tmdb.org/t/p/w500${show.poster_path}`}
                              alt={show.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                                <Play className="text-white" size={28} fill="white" />
                              </div>
                            </div>
                            <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                              <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                              <span className="text-white mont text-xs font-semibold">
                                {show.vote_average?.toFixed(1)}
                              </span>
                            </div>
                          </div>
                          <div className="p-3">
                            <h3 className="text-white mont font-semibold text-[14px] leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                              {show.name}
                            </h3>
                            {show.first_air_date && (
                              <p className="text-white/50 mont text-xs mt-1">
                                {new Date(show.first_air_date).getFullYear()}
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
          </div>

          {/* Anime Section */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white mont font-bold text-2xl md:text-3xl">
                Trending Anime <span className="text-red">🔥</span>
              </h2>
              <Link
                to="/anime"
                className="text-white/60 hover:text-red mont font-medium text-sm transition-colors"
              >
                View All →
              </Link>
            </div>
            
            <div className="relative">
              {loading ? (
                <div className="grid lg:grid-cols-6 md:grid-cols-4 sm:grid-cols-3 grid-cols-2 gap-4 md:gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <MovieCardSkeleton key={index} />
                  ))}
                </div>
              ) : (
                <Swiper
                  spaceBetween={20}
                  slidesPerView={5}
                  navigation={true}
                  breakpoints={{
                    230: { slidesPerView: 2, spaceBetween: 8 },
                    480: { slidesPerView: 3, spaceBetween: 10 },
                    640: { slidesPerView: 3, spaceBetween: 12 },
                    768: { slidesPerView: 4, spaceBetween: 15 },
                    1000: { slidesPerView: 5, spaceBetween: 18 },
                    1280: { slidesPerView: 6, spaceBetween: 20 },
                  }}
                  modules={[Navigation]}
                  className="modern-swiper"
                >
                  {anime.filter(animeItem => animeItem.poster_path).map((animeItem) => (
                    <SwiperSlide key={animeItem.id} className="modern-movie-slide">
                      <Link to={`/anime/${animeItem.id}`} className="group">
                        <div className="relative overflow-hidden rounded-lg bg-gray/30 movie-card-wrapper">
                          <div className="relative aspect-[2/3] overflow-hidden bg-gradient-to-br from-gray/40 to-gray/20">
                            <img
                              src={`https://image.tmdb.org/t/p/w500${animeItem.poster_path}`}
                              alt={animeItem.name}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                                <Play className="text-white" size={28} fill="white" />
                              </div>
                            </div>
                            <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
                              <Star className="text-yellow-400" size={12} fill="#FBBF24" />
                              <span className="text-white mont text-xs font-semibold">
                                {animeItem.vote_average?.toFixed(1)}
                              </span>
                            </div>
                          </div>
                          <div className="p-3">
                            <h3 className="text-white mont font-semibold text-[14px] leading-tight line-clamp-2 group-hover:text-red transition-colors duration-300">
                              {animeItem.name}
                            </h3>
                            {animeItem.first_air_date && (
                              <p className="text-white/50 mont text-xs mt-1">
                                {new Date(animeItem.first_air_date).getFullYear()}
                              </p>
                            )}
                          </div>
                        </div>
                      </Link>
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default HomePage;
